import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const EstadoBadge = ({ estado }) => {
  if (estado === 'presente') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <i className="fas fa-graduation-cap"></i> Presente
      </span>
    );
  }
  if (estado === 'video') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
        <i className="fas fa-video"></i> Video
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
      <i className="fas fa-times-circle"></i> No Presente
    </span>
  );
};

const AsistenciasClases = () => {
  const [clases, setClases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedClase, setSelectedClase] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [loadingResumen, setLoadingResumen] = useState(false);
  const [expandedSesion, setExpandedSesion] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [reporteOpen, setReporteOpen] = useState(false);
  const [reporteClase, setReporteClase] = useState(null);
  const [reporteLoading, setReporteLoading] = useState(null); // "descargar" | "mega" | "ambas" | null

  useEffect(() => {
    const userDataStr = localStorage.getItem('cached_user');
    if (userDataStr) {
      try {
        const parsedUser = JSON.parse(userDataStr);
        setUser(parsedUser);
        cargarClases(parsedUser);
      } catch (e) {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  }, []);

  const cargarClases = async (currentUser) => {
    if (!currentUser) { setLoading(false); return; }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userRol = currentUser?.rol;
      const userId = currentUser?.id;
      let clasesFiltered = [];
      if (userRol === 'admin') {
        clasesFiltered = response.data.filter(c => c.activa);
      } else if (userRol === 'profesor') {
        clasesFiltered = response.data.filter(c => c.activa && c.profesor_id === userId);
      }
      setClases(clasesFiltered);
    } catch (error) {
      toast.error('Error al cargar las clases');
    } finally {
      setLoading(false);
    }
  };

  const verAsistencias = async (clase) => {
    setSelectedClase(clase);
    setLoadingResumen(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/asistencias/clase/${clase.id}/resumen`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResumen(response.data);
    } catch (error) {
      toast.error('Error al cargar resumen de asistencias');
      setResumen(null);
    } finally {
      setLoadingResumen(false);
    }
  };

  const cerrarDetalle = () => {
    setSelectedClase(null);
    setResumen(null);
  };

  const toggleAsistencia = async (alumnoId, fecha, estadoActual) => {
    const nuevoEstado = estadoActual === 'ausente' || estadoActual === 'video' ? 'presente' : 'ausente';
    const key = `${alumnoId}-${fecha}`;
    setTogglingId(key);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/asistencias/toggle`, {
        clase_id: selectedClase.id,
        estudiante_id: alumnoId,
        fecha: fecha,
        nuevo_estado: nuevoEstado
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(nuevoEstado === 'presente' ? 'Asistencia marcada como presente' : 'Asistencia marcada como ausente');
      // Reload resumen
      verAsistencias(selectedClase);
    } catch {
      toast.error('Error al cambiar asistencia');
    } finally {
      setTogglingId(null);
    }
  };

  const isAdmin = user?.rol === 'admin';

  const abrirReporte = (clase, e) => {
    e.stopPropagation();
    setReporteClase(clase);
    setReporteOpen(true);
  };

  const cerrarReporte = () => {
    if (reporteLoading) return;
    setReporteOpen(false);
    setReporteClase(null);
  };

  const generarReporte = async (destino) => {
    if (!reporteClase) return;
    setReporteLoading(destino);
    const token = localStorage.getItem('token');
    try {
      if (destino === 'mega') {
        const r = await axios.post(
          `${API}/asistencias/clase/${reporteClase.id}/reporte-pdf`,
          { destino: 'mega' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Subido a Mega: ${r.data?.folder || 'IBA Connect Reportes/Asistencia'}`);
        setReporteOpen(false);
        setReporteClase(null);
      } else {
        // descargar o ambas: el backend devuelve el PDF como blob
        const r = await axios.post(
          `${API}/asistencias/clase/${reporteClase.id}/reporte-pdf`,
          { destino },
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob',
          }
        );
        const blob = new Blob([r.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const safeTitle = (reporteClase.titulo || 'Clase').replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 60);
        link.href = url;
        link.download = `Asistencia_${safeTitle}.pdf`;
        link.rel = 'noopener';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        // Limpieza diferida: revocar la URL recién después de que el browser
        // haya arrancado la descarga. Si revocamos sincronicamente, algunos
        // navegadores cancelan la descarga.
        setTimeout(() => {
          try { document.body.removeChild(link); } catch (_) {}
          try { window.URL.revokeObjectURL(url); } catch (_) {}
        }, 4000);
        if (destino === 'ambas') {
          toast.success('PDF descargado y subiendo a Mega en segundo plano');
        } else {
          toast.success('PDF descargado');
        }
        // Cerramos el modal con un pequeño delay para que el browser muestre
        // el banner de descarga sin que el modal lo "robe" visualmente.
        setTimeout(() => {
          setReporteOpen(false);
          setReporteClase(null);
        }, 600);
      }
    } catch (err) {
      const msg = err.response?.status === 502
        ? 'No se pudo subir a Mega. Intentá de nuevo o usá "Solo descargar".'
        : (err.response?.data?.detail || 'Error al generar el reporte');
      toast.error(msg);
    } finally {
      setReporteLoading(null);
    }
  };


  const formatFecha = (fechaStr) => {
    const date = new Date(fechaStr + 'T12:00:00');
    return date.toLocaleDateString('es-UY', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600 mb-4"></i>
          <p className="text-gray-600">Cargando clases...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button 
        variant="ghost" 
        onClick={() => selectedClase ? cerrarDetalle() : (window.history.length > 2 ? window.history.back() : window.location.href = '/app/dashboard')}
        className="text-gray-600 hover:text-gray-800"
        data-testid="back-button"
      >
        <i className="fas fa-arrow-left mr-2"></i>
        {selectedClase ? 'Volver a Clases' : 'Volver'}
      </Button>

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-emerald-700 rounded-xl p-6 text-white shadow-lg">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/20 rounded-xl">
            <i className="fas fa-clipboard-list text-3xl"></i>
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold" data-testid="asistencias-title">
              {selectedClase ? `Asistencias - ${selectedClase.titulo}` : 'Asistencias de Clases'}
            </h1>
            <p className="text-teal-100">
              {resumen 
                ? `${resumen.total_inscritos} inscritos | ${resumen.total_sesiones} sesiones registradas`
                : isAdmin 
                  ? 'Control de asistencia de todas las clases activas'
                  : 'Control de asistencia de tus clases'
              }
            </p>
          </div>
          {selectedClase && (
            <Button
              type="button"
              onClick={(e) => abrirReporte(selectedClase, e)}
              className="bg-white text-teal-700 hover:bg-teal-50 font-semibold flex-shrink-0"
              data-testid="generar-reporte-detalle-btn"
            >
              <i className="fas fa-file-pdf mr-2"></i>
              Generar Reporte PDF
            </Button>
          )}
        </div>
        {selectedClase && (
          <div className="flex gap-4 mt-3 text-sm">
            <span className="flex items-center gap-1"><i className="fas fa-graduation-cap"></i> Presente = Asistio a clase</span>
            <span className="flex items-center gap-1"><i className="fas fa-video"></i> Video = No asistio pero vio el video</span>
            <span className="flex items-center gap-1"><i className="fas fa-times-circle"></i> No Presente = No asistio ni vio video</span>
          </div>
        )}
      </div>

      {/* Detail view */}
      {selectedClase ? (
        <div className="space-y-4">
          {loadingResumen ? (
            <div className="flex items-center justify-center py-12">
              <i className="fas fa-spinner fa-spin text-3xl text-teal-600"></i>
            </div>
          ) : !resumen || resumen.sesiones.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <i className="fas fa-clipboard-list text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">Sin sesiones registradas</h3>
                <p className="text-gray-500">Aun no hay sesiones de clase registradas. Las sesiones aparecen automaticamente segun el horario de la clase.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
            {resumen.sesiones.map((sesion, idx) => {
              const isOpen = expandedSesion === sesion.fecha;
              return (
              <Card key={sesion.fecha} className="border-0 shadow-md overflow-hidden" data-testid={`sesion-${sesion.fecha}`}>
                <button
                  onClick={() => setExpandedSesion(isOpen ? null : sesion.fecha)}
                  className="w-full bg-gray-50 px-4 py-3 border-b flex items-center justify-between hover:bg-gray-100 transition-colors cursor-pointer"
                  data-testid={`toggle-sesion-${sesion.fecha}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {resumen.sesiones.length - idx}
                    </div>
                    <h3 className="font-semibold text-gray-800 text-sm capitalize">{formatFecha(sesion.fecha)}</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-2 text-xs">
                      <span className="flex items-center gap-1 text-green-600 font-medium">
                        <i className="fas fa-graduation-cap"></i> {sesion.total_presentes}
                      </span>
                      <span className="flex items-center gap-1 text-blue-600 font-medium">
                        <i className="fas fa-video"></i> {sesion.total_video}
                      </span>
                      <span className="flex items-center gap-1 text-red-600 font-medium">
                        <i className="fas fa-times-circle"></i> {sesion.total_ausentes}
                      </span>
                    </div>
                    <i className={`fas fa-chevron-down text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}></i>
                  </div>
                </button>
                {isOpen && (
                  <CardContent className="p-0">
                    <div className="divide-y">
                      {sesion.alumnos.map((alumno) => (
                        <div key={alumno.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors">
                          <div className="flex items-center gap-3 min-w-0 flex-1">
                            <span className="text-sm text-gray-800 truncate">{alumno.nombre}</span>
                            {alumno.hora_registro && (
                              <span className="text-xs text-gray-400 flex-shrink-0">
                                <i className="fas fa-clock mr-1"></i>{alumno.hora_registro}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <EstadoBadge estado={alumno.estado} />
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleAsistencia(alumno.id, sesion.fecha, alumno.estado); }}
                              disabled={togglingId === `${alumno.id}-${sesion.fecha}`}
                              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs transition-colors ${
                                alumno.estado === 'ausente' || alumno.estado === 'video'
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                              } ${togglingId === `${alumno.id}-${sesion.fecha}` ? 'opacity-50' : ''}`}
                              title={alumno.estado === 'ausente' || alumno.estado === 'video' ? 'Marcar presente' : 'Marcar ausente'}
                              data-testid={`toggle-${alumno.id}-${sesion.fecha}`}
                            >
                              {togglingId === `${alumno.id}-${sesion.fecha}` 
                                ? <i className="fas fa-spinner fa-spin"></i>
                                : alumno.estado === 'ausente' || alumno.estado === 'video'
                                  ? <i className="fas fa-check"></i>
                                  : <i className="fas fa-times"></i>
                              }
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
              );
            })}
            </div>
          )}
        </div>
      ) : (
        /* Grid of class cards */
        <>
          {clases.length === 0 ? (
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <i className="fas fa-clipboard-list text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No hay clases disponibles</h3>
                <p className="text-gray-500">
                  {isAdmin ? 'No hay clases activas en el sistema.' : 'No tienes clases asignadas actualmente.'}
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clases.map((clase) => (
                <Card 
                  key={clase.id} 
                  className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                  onClick={() => verAsistencias(clase)}
                  data-testid={`asistencia-clase-${clase.id}`}
                >
                  <div className="relative h-40 bg-gradient-to-br from-teal-500 to-emerald-600 overflow-hidden">
                    {clase.banner_url ? (
                      <img src={clase.banner_url} alt={clase.titulo} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="fas fa-graduation-cap text-5xl text-white/50"></i>
                      </div>
                    )}
                    {isAdmin && (
                      <div className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium ${
                        clase.categoria === 'profesores' ? 'bg-purple-600 text-white' : 'bg-teal-600 text-white'
                      }`}>
                        {clase.categoria === 'profesores' ? 'Capacitacion' : 'Estudiantes'}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 rounded-full bg-teal-600 flex items-center justify-center">
                        <i className="fas fa-clipboard-list text-white text-2xl"></i>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-4">
                    <h3 className="font-bold text-lg text-gray-800 mb-2 line-clamp-2">{clase.titulo}</h3>

                    {clase.profesor_nombre && (
                      <div className="flex items-center gap-2 mb-3 text-sm text-gray-600">
                        <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                          {clase.profesor_foto ? (
                            <img src={clase.profesor_foto} alt={clase.profesor_nombre} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-400">
                              <i className="fas fa-user"></i>
                            </div>
                          )}
                        </div>
                        <span className="truncate">{clase.profesor_nombre}</span>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                      <span><i className="fas fa-users mr-1"></i> {clase.estudiantes_inscritos?.length || 0} inscritos</span>
                      <span><i className="fas fa-calendar mr-1"></i> {clase.dias_semana?.join(', ') || '-'}</span>
                    </div>

                    <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid={`ver-asistencias-${clase.id}`}>
                      <i className="fas fa-clipboard-list mr-2"></i>
                      Ver Asistencias
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={(e) => abrirReporte(clase, e)}
                      className="w-full mt-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                      data-testid={`generar-reporte-${clase.id}`}
                    >
                      <i className="fas fa-file-pdf mr-2"></i>
                      Generar Reporte PDF
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Modal: Generar Reporte PDF */}
      {reporteOpen && reporteClase && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={cerrarReporte}
          data-testid="reporte-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="reporte-modal"
          >
            {/* Encabezado del modal */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-700 p-5 text-white">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <i className="fas fa-file-pdf text-2xl"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg leading-tight">Reporte Oficial de Asistencia</h3>
                  <p className="text-xs text-indigo-100 truncate">{reporteClase.titulo}</p>
                </div>
              </div>
            </div>

            {/* Cuerpo */}
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 leading-relaxed">
                Se generará un PDF oficial con el logo del Instituto, ordenado por estudiante,
                con los datos al día de la última clase dictada. Si subís a Mega, se reemplazará
                el reporte anterior de esta clase.
              </p>

              <div className="space-y-2 pt-2">
                <Button
                  type="button"
                  onClick={() => generarReporte('ambas')}
                  disabled={!!reporteLoading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white justify-start h-auto py-3"
                  data-testid="reporte-ambas-btn"
                >
                  {reporteLoading === 'ambas' ? (
                    <i className="fas fa-spinner fa-spin mr-3"></i>
                  ) : (
                    <i className="fas fa-bolt mr-3 text-lg"></i>
                  )}
                  <span className="flex flex-col items-start">
                    <span className="font-semibold">Descargar y subir a Mega</span>
                    <span className="text-xs opacity-90 font-normal">Recomendado · descarga al instante + sube en segundo plano</span>
                  </span>
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => generarReporte('descargar')}
                  disabled={!!reporteLoading}
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 justify-start h-auto py-3"
                  data-testid="reporte-descargar-btn"
                >
                  {reporteLoading === 'descargar' ? (
                    <i className="fas fa-spinner fa-spin mr-3"></i>
                  ) : (
                    <i className="fas fa-download mr-3 text-lg text-gray-600"></i>
                  )}
                  <span className="flex flex-col items-start">
                    <span className="font-semibold">Solo descargar</span>
                    <span className="text-xs text-gray-500 font-normal">Guarda el PDF en tu dispositivo</span>
                  </span>
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  onClick={() => generarReporte('mega')}
                  disabled={!!reporteLoading}
                  className="w-full border-gray-300 text-gray-700 hover:bg-gray-50 justify-start h-auto py-3"
                  data-testid="reporte-mega-btn"
                >
                  {reporteLoading === 'mega' ? (
                    <i className="fas fa-spinner fa-spin mr-3"></i>
                  ) : (
                    <i className="fas fa-cloud-upload-alt mr-3 text-lg text-gray-600"></i>
                  )}
                  <span className="flex flex-col items-start">
                    <span className="font-semibold">Solo subir a Mega</span>
                    <span className="text-xs text-gray-500 font-normal">IBA Connect Reportes / Asistencia</span>
                  </span>
                </Button>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-5 py-3 flex justify-end border-t border-gray-100">
              <Button
                type="button"
                variant="ghost"
                onClick={cerrarReporte}
                disabled={!!reporteLoading}
                className="text-gray-600 hover:text-gray-800"
                data-testid="reporte-cancelar-btn"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AsistenciasClases;
